import * as React from 'react';
import AttachmentItemsSort from 'owa-service/lib/contract/AttachmentItemsSort';
import AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import AttachmentItemsSortOrder from 'owa-service/lib/contract/AttachmentItemsSortOrder';
import FileProviderItemViewState from '../store/schema/FileProviderItemViewState';
import getAriaLabelForFileProviderItem from './utils/getAriaLabelForFileProviderItem';
import ItemRowData from '../types/ItemRowData';
import LoadMoreDetailsList from 'owa-loadmore-detailslist/lib/components/LoadMoreDetailsList';
import strings from 'owa-strings/owa-mail-strings';
import { FileIcon, getFileSpriteIconClass } from 'owa-file-icon';
import { format } from 'owa-strings';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { observer } from 'mobx-react';
import {
    FilePickerListColumnInfo,
    NAME_PROPERTY_NAME,
    FROM_PROPERTY_NAME,
    SUBJECT_PROPERTY_NAME,
    ICON_PROPERTY_NAME,
    LOCATION_ICON_PROPERTY_NAME,
    PREVIEW_PROPERTY_NAME,
} from '../utils/fileProviderInfo/filePickerListColumnInfo';
import {
    buildColumns,
    CheckboxVisibility,
    ColumnActionsMode,
    ConstrainMode,
    DetailsListLayoutMode,
    DetailsRow,
    IColumn,
    IDetailsListProps,
    IDetailsRowProps,
    IDetailsRow,
} from 'office-ui-fabric-react/lib/DetailsList';
import {
    IObjectWithKey,
    ISelection,
    Selection,
} from 'office-ui-fabric-react/lib/utilities/selection';

const classNames = require('classnames');
const styles = require('./FilePickerListView.scss');

// columns that we have special treatment for when we render them, and columns that we don't render
const KeyColumnName = 'key';
const MetadataColumnName = 'metadata';

export interface FilePickerListViewProps {
    dataSourceId: string;
    getItemToRender: (Item: FileProviderItemViewState, index: number) => ItemRowData;
    items: FileProviderItemViewState[];
    selectedItems: FileProviderItemViewState[];
    onLoadMoreRows: () => void;
    isLoadRowsInProgress: boolean;
    getCanLoadMore: () => boolean;
    getFilePickerListColumnInfo: (key: string) => FilePickerListColumnInfo;
    currentSorting: AttachmentItemsSort;
    isAnyItemSelected: boolean;
    onItemSelected?: (item: FileProviderItemViewState) => void;
    onItemsSelectionUpdated?: (selectedItems: FileProviderItemViewState[]) => void;
    sortByColumn?: (
        sortColumn: AttachmentItemsSortColumn,
        customSort?: (
            items: FileProviderItemViewState[],
            sortAsc: boolean
        ) => FileProviderItemViewState[]
    ) => void;
}

export interface FilePickerListViewState {
    // The selection model is used to track the selection state of the files in the file picker
    selection: ISelection;
}

@observer
export default class FilePickerListView extends React.Component<
    FilePickerListViewProps,
    FilePickerListViewState
> {
    row: IDetailsRow[] = [];
    shouldSetFocus: boolean;

    constructor(props: FilePickerListViewProps) {
        super(props);

        this.state = {
            selection: new Selection({
                onSelectionChanged: this.onItemsSelectionChanged,
                getKey: this.getKeyForItem,
            }),
        };
    }

    render() {
        this.row = [];
        const items = this.props.items.map(this.props.getItemToRender);
        const detailslistProps: IDetailsListProps = {
            setKey: this.props.dataSourceId,
            columns: this.buildColumnsForData(items),
            constrainMode: ConstrainMode.unconstrained,
            layoutMode: DetailsListLayoutMode.justified,
            items: items,
            onRenderRow: this.onRenderRow,
            getRowAriaLabel: this.getAriaLabelForItem,
            onRenderItemColumn: this.onRenderItemColumn,
            onItemInvoked: this.onItemSelected,
            selection: this.state.selection,
            initialFocusedIndex: 0,
            ariaLabel: format(strings.ListViewAriaLabelText, this.props.items.length),
            ariaLabelForListHeader: format(strings.ListViewAriaLabelText, this.props.items.length),
        };

        return (
            <div className={styles.container}>
                <LoadMoreDetailsList
                    className={classNames(styles.filePickerList, 'customScrollBar')}
                    dataSourceId={this.props.dataSourceId}
                    onLoadMoreRows={this.props.onLoadMoreRows}
                    isLoadRowsInProgress={this.props.isLoadRowsInProgress}
                    getCanLoadMore={this.props.getCanLoadMore}
                    currentLoadedIndex={items.length}
                    detailsListProps={detailslistProps}
                />
            </div>
        );
    }

    componentDidMount() {
        // When the component mounts we need to set the items and the selected items
        // so that if the user switches between the view the selection is still maintained.
        // As the items passed to details list is not exactly the FileProviderItemViewState[]
        // hence we need to have the same items set in selection.
        const items = this.props.items.map(this.props.getItemToRender);
        this.state.selection.setItems(items, true);
        this.resetSelectedItems(this.props.selectedItems, this.state.selection);
    }

    componentWillReceiveProps(nextProps: FilePickerListViewProps) {
        // If something has changed in selected items then we reset the selection with the new selected items
        const shouldResetSelectedItems =
            nextProps.selectedItems.some(
                nextSelectedItem => this.props.selectedItems.indexOf(nextSelectedItem) === -1
            ) || nextProps.selectedItems.length === 0;
        if (shouldResetSelectedItems) {
            this.resetSelectedItems(nextProps.selectedItems, this.state.selection);
        }

        // If we are loading new items we should set the focus on the first item. We set a flag now, and set focus once the items are loaded.
        // We make sure that our current item list is empty, so that we don't reset focus while loading more items from pagination.
        if (this.props.items.length === 0 && nextProps.items.length !== 0) {
            this.shouldSetFocus = true;
        }
    }

    componentDidUpdate() {
        if (this.shouldSetFocus) {
            this.focus();
            this.shouldSetFocus = false;
        }
    }

    public focus() {
        if (this.row[0]) {
            (this.row[0] as any).focus();
        }
    }

    /* Renders a single row for the file provider item */
    private onRenderRow = (props: IDetailsRowProps): JSX.Element => {
        const correspondingFileProviderItem = this.props.items[props.itemIndex];
        props.checkboxVisibility = this.props.isAnyItemSelected
            ? CheckboxVisibility.always
            : CheckboxVisibility.onHover;

        return (
            <div className={styles.filePickerRow} key={correspondingFileProviderItem.key}>
                <DetailsRow
                    componentRef={ref => (this.row = this.row.concat(ref))}
                    {...props}
                    styles={{ cellUnpadded: styles.cellUnpadded }}
                />
            </div>
        );
    };

    /* Renders the column for each of the data in a single row */
    private onRenderItemColumn = function onRenderItemColumn(
        item: ItemRowData,
        index: number,
        column: IColumn
    ): JSX.Element {
        const isUnread = item.metadata.isUnread;

        const isUnreadSubjectStyle = isUnread ? styles.unreadSubject : null;
        const isUnreadSenderStyle = isUnread ? styles.unreadSender : null;

        // In case of Icon we create an image for that icon otherwise we just wrap the content in an element
        switch (column.key) {
            case ICON_PROPERTY_NAME:
                // In case of icon the fieldContent will be the css class for that icon
                const icon: FileIcon = item[column.fieldName];
                const iconClasses = classNames([styles.fileProviderItemIcon, icon.small]);
                return <button className={iconClasses} data-is-focusable={false} />;
            case LOCATION_ICON_PROPERTY_NAME:
                return (
                    <Icon
                        className={classNames(
                            styles.fileProviderItemIcon,
                            styles.fileProviderLocationIcon
                        )}
                        iconName={item[column.fieldName]}
                    />
                );
            case NAME_PROPERTY_NAME:
                const nameFieldContent: string = item[column.fieldName];
                return (
                    <span className={styles.filePickerRowNameCell} title={item.metadata.title}>
                        {nameFieldContent}
                    </span>
                );
            case SUBJECT_PROPERTY_NAME:
                return (
                    <span
                        className={classNames(styles.filePickerRowNameCell, isUnreadSubjectStyle)}
                        title={item[column.fieldName]}>
                        {item[column.fieldName]}
                    </span>
                );
            case FROM_PROPERTY_NAME:
                return (
                    <span
                        className={classNames(styles.filePickerRowNameCell, isUnreadSenderStyle)}
                        title={item[column.fieldName]}>
                        {item[column.fieldName]}
                    </span>
                );
            case PREVIEW_PROPERTY_NAME:
                return (
                    <span className={styles.filePickerRowNameCell} title={item[column.fieldName]}>
                        {item[column.fieldName]}
                    </span>
                );
            default:
                const fieldContent: string = item[column.fieldName];
                return <span>{fieldContent}</span>;
        }
    };

    private buildColumnsForData = (items: any): IColumn[] => {
        const columns = buildColumns(items, false);
        const getColumnInfo = this.props.getFilePickerListColumnInfo;

        // Set the column headers and widths
        columns.forEach(column => {
            column.name = getColumnInfo(column.key).name;
            column.maxWidth = getColumnInfo(column.key).maxWidth;
            column.minWidth = getColumnInfo(column.key).minWidth;
            column.isSorted =
                this.props.currentSorting.SortColumn === getColumnInfo(column.key).sort;
            column.isSortedDescending =
                this.props.currentSorting.SortOrder === AttachmentItemsSortOrder.Descending;
            if (column.key === ICON_PROPERTY_NAME) {
                column.iconClassName = classNames(
                    getFileSpriteIconClass('generic_16x16'),
                    styles.iconColumnHeaderIcon
                );
                column.headerClassName = styles.iconColumnHeader;
                column.columnActionsMode = ColumnActionsMode.disabled;
            } else if (getColumnInfo(column.key).clientSort) {
                column.headerClassName = classNames(styles.column, styles.clickableColumnHeader);
                column.onColumnClick = () => {
                    const columnInfo = getColumnInfo(column.key);
                    this.props.sortByColumn(columnInfo.sort, columnInfo.clientSort);
                };
            } else if (getColumnInfo(column.key).sort === null) {
                column.columnActionsMode = ColumnActionsMode.disabled;
                column.headerClassName = styles.column;
            } else {
                column.headerClassName = classNames(styles.column, styles.clickableColumnHeader);
                column.onColumnClick = () =>
                    this.props.sortByColumn(getColumnInfo(column.key).sort, null);
            }
        });

        // Removing the key column as we don't want to display that to user
        return columns.filter(
            (column: IColumn) => column.key !== MetadataColumnName && column.key !== KeyColumnName
        );
    };

    private getAriaLabelForItem = (item: ItemRowData) => {
        const correspondingFileProviderItem = this.props.items[item.metadata.index];
        return getAriaLabelForFileProviderItem(correspondingFileProviderItem);
    };

    private handleItemSelected = (item: FileProviderItemViewState) => {
        if (this.props.onItemSelected) {
            this.props.onItemSelected(item);
        }
    };

    /* Called when an item is selected by pressing enter on the row */
    private onItemSelected = (item: ItemRowData, index: number, evt: Event) => {
        if (item && index < this.props.items.length) {
            const correspondingFileProviderItem = this.props.items[index];
            this.handleItemSelected(correspondingFileProviderItem);
        }
    };

    /* Use to create a key corresponding to an item when it is selected */
    private getKeyForItem = (item: any, index: number): string => {
        // We return the index as key so that we can map it easily to FileProviderItemViewState
        return index.toString();
    };

    private resetSelectedItems = (
        selectedItems: FileProviderItemViewState[],
        selection: ISelection
    ) => {
        // We need to disable the trigger of change event so during the process of resetting the selection
        // it does not trigger the change event
        selection.setChangeEvents(false);

        // Setting all items to unselected state
        selection.setAllSelected(false);

        // Getting the indexes of selected items and then setting selection to true for them
        selectedItems
            .map(selectedItem => this.props.items.indexOf(selectedItem))
            .forEach(selectedItemIndex =>
                selection.setIndexSelected(
                    selectedItemIndex,
                    true /* isSelected */,
                    false /* shouldAnchor */
                )
            );

        // We enable the change events when we are done
        selection.setChangeEvents(true);
    };

    /* Called when the user selects items in the file picker */
    private onItemsSelectionChanged = () => {
        const selectedItem = this.state.selection.getSelection();

        // Get the FileProviderItemViewState corresponding to the selected items
        const selectedFileProviderItems: FileProviderItemViewState[] = selectedItem.map(
            (selectedItemKey: IObjectWithKey) =>
                this.props.items[Number(selectedItemKey[MetadataColumnName].index)]
        );

        if (this.props.onItemsSelectionUpdated) {
            this.props.onItemsSelectionUpdated(selectedFileProviderItems);
        }
    };
}
